import { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api } from './api';
import { Dashboard } from './pages/Dashboard';
import { ClientDetail } from './pages/ClientDetail';
import { Login } from './pages/Login';
import { Report } from './pages/Report';

type AuthState = 'checking' | 'in' | 'out';

export function App() {
  const [auth, setAuth] = useState<AuthState>('checking');
  const location = useLocation();

  // Report pages are public: they are embedded in the client's own dashboard,
  // where the reader has no account here. Checked before the session probe so
  // an unauthenticated viewer never sees a login flash, and never gets
  // redirected away from their report.
  const isReport = location.pathname.startsWith('/r/');

  useEffect(() => {
    if (isReport) return;
    api
      .session()
      .then(() => setAuth('in'))
      .catch(() => setAuth('out'));
  }, [isReport]);

  const signedOut = useCallback(() => setAuth('out'), []);
  const signedIn = useCallback(() => setAuth('in'), []);

  if (isReport) {
    // Rendered bare: no .shell wrapper, no admin chrome. The page fills the
    // iframe it is embedded in.
    return (
      <Routes>
        <Route path="/r/:token" element={<Report />} />
      </Routes>
    );
  }

  if (auth === 'checking') {
    return (
      <div className="shell">
        <div className="spinner">Loading…</div>
      </div>
    );
  }

  if (auth === 'out') {
    return (
      <div className="shell">
        <Routes>
          <Route path="/login" element={<Login onSignedIn={signedIn} />} />
          {/* Preserve where the operator was headed, including the query
              params an OAuth callback redirects back with. */}
          <Route
            path="*"
            element={
              <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
            }
          />
        </Routes>
      </div>
    );
  }

  return (
    <div className="shell">
      <Routes>
        <Route path="/" element={<Dashboard onSignedOut={signedOut} />} />
        <Route path="/clients/:slug" element={<ClientDetail onSignedOut={signedOut} />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
